import { useEffect, useRef, useState } from 'react';
import { useRachelPresentation } from '../../hooks/useRachelPresentation';
import { RachelUIMessage } from '../../rachel.types';
import { UseRachelConversationListParams } from './rachelConversationList.types';

const BOTTOM_THRESHOLD = 24;

const countNewAssistantMessages = (messages: RachelUIMessage[], seenLength: number) =>
  messages.slice(seenLength).filter((message) => message.role === 'assistant').length;

const isScrolledToBottom = (node: HTMLElement) =>
  node.scrollHeight - node.scrollTop - node.clientHeight <= BOTTOM_THRESHOLD;

export const useRachelConversationList = ({
  messages,
  status,
  onBottomVisibilityChange,
}: UseRachelConversationListParams) => {
  const { isMaximized } = useRachelPresentation();
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const isFollowingRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const messagesLengthRef = useRef(messages.length);
  const seenLengthRef = useRef(messages.length);

  const scrollToBottom = () => {
    scrollNode?.scrollTo({ top: scrollNode.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    messagesLengthRef.current = messages.length;

    if (!scrollNode) {
      return;
    }

    if (!isFollowingRef.current) {
      setNewMessagesCount(countNewAssistantMessages(messages, seenLengthRef.current));
      return;
    }

    scrollNode.scrollTop = scrollNode.scrollHeight;
    lastScrollTopRef.current = scrollNode.scrollTop;
    seenLengthRef.current = messages.length;
    setNewMessagesCount(0);
  }, [scrollNode, messages, status]);

  useEffect(() => {
    if (!scrollNode) {
      onBottomVisibilityChange(true);
      return;
    }

    const setFollowing = (isFollowing: boolean) => {
      isFollowingRef.current = isFollowing;
      setIsAtBottom(isFollowing);
      onBottomVisibilityChange(isFollowing);

      if (isFollowing) {
        seenLengthRef.current = messagesLengthRef.current;
        setNewMessagesCount(0);
      }
    };

    const handleScroll = () => {
      const previousScrollTop = lastScrollTopRef.current;
      const { scrollTop } = scrollNode;
      lastScrollTopRef.current = scrollTop;

      if (isScrolledToBottom(scrollNode)) {
        setFollowing(true);
        return;
      }

      if (scrollTop < previousScrollTop) {
        setFollowing(false);
      }
    };

    scrollNode.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollNode.removeEventListener('scroll', handleScroll);
  }, [scrollNode, onBottomVisibilityChange]);

  return {
    isAtBottom,
    newMessagesCount,
    hasCenteredColumn: isMaximized,
    scrollToBottom,
    setScrollNode,
  };
};
